import React from 'react';

import { Button } from '@eventbrite/eds-button';
import { CalendarGraphic } from '@eventbrite/eds-iconography';
import { EmptyState } from '@eventbrite/eds-empty-state';
import {
    EventCard,
    useInfiniteEvents,
    transformDestinationEvent,
    CONSUMER_CARD_DEFAULTS,
} from '@eventbrite/event-renderer';
import { gettext } from '@eventbrite/i18n';

import { getCollectionEventsWithSearch } from '../../api/events';
import EventCardGroupEdit from '../../components/EventCardGroupEdit';
import {
    EVENTCARD_WRAPPER_CLASSES,
    FOLLOW_SIGNAL_EXPERIMENT,
} from './constants';

const fetchPersonalCollectionEvents = async (collectionId, continuation) => {
    const results = await getCollectionEventsWithSearch({
        id: collectionId,
        continuation,
    });

    return {
        pagination: { continuation: results.events.pagination.continuation },
        events: results.events.results.map((event) =>
            transformDestinationEvent(event),
        ),
    };
};

export const PersonalEventRenderer = (props) => {
    const { data, isFetchingNextPage, hasNextPage, fetchNextPage } =
        useInfiniteEvents(
            ['personal-collection-events', props.userId, props.collectionId],
            ({ pageParam }) =>
                fetchPersonalCollectionEvents(props.collectionId, pageParam),
            {
                getNextPageParam: (lastGroup) =>
                    lastGroup.pagination.continuation || undefined,
                initialData: {
                    pages: [
                        {
                            events: props.events.events.map((event) =>
                                transformDestinationEvent(event),
                            ),
                            pagination: {
                                continuation:
                                    props.events.pagination.continuation,
                            },
                        },
                    ],
                    pageParams: [props.events.pagination.continuation],
                },
                expandEventsOnMount: true,
            },
        );

    if (!data || data?.pages?.[0]?.events?.length === 0) {
        return (
            <section>
                <EmptyState
                    graphicType={<CalendarGraphic />}
                    description={gettext('Your calendar is wide open!')}
                    title={gettext("Let's find your next favorite thing.")}
                />
            </section>
        );
    }

    if (props.isEditMode) {
        return (
            <EventCardGroupEdit
                name="eventsToRemove"
                events={data.pages.reduce((memo, page) => {
                    return [...memo, ...page.events];
                }, [])}
                isEditMode={props.isEditMode}
            />
        );
    }

    return (
        <section className="eds-group personal-event-renderer">
            {data.pages.map((page) =>
                page.events.map((eventData) => (
                    <div
                        className={EVENTCARD_WRAPPER_CLASSES}
                        key={eventData.id}
                    >
                        <EventCard
                            {...CONSUMER_CARD_DEFAULTS}
                            affCode={props.affCode}
                            eventData={eventData}
                            key={eventData.id}
                            gaCategory={props.gaCategory}
                            isAuthenticated={props.isAuthenticated}
                            user={{ id: props.userId }}
                        />
                    </div>
                )),
            )}
            {hasNextPage && (
                <div className="eds-align--center eds-l-pad-bot-4">
                    <Button
                        style="link"
                        onClick={() => fetchNextPage()}
                        isLoading={!!isFetchingNextPage}
                    >
                        {gettext('Show More')}
                    </Button>
                </div>
            )}
        </section>
    );
};
